import { IRecordOf } from '@opengov/compose-app/immutable';
import { IOgqlViewDetail } from '@opengov/ogql-types';
import { EEditMode, enumActiveNavigationPanelFilter } from '../../../src/types/MultiDatasetTypes';
import {
  CalendarUnit,
  ColumnValueSegment,
  EGroupType,
  EReportChartType,
  IReportConfiguration,
  IReportDatasetLens,
  IRowGroup,
  IColumnDataSource,
  MultiHierarchyColumnDataSource
} from '@opengov/reporting-types';
import { Map } from 'immutable';
import { IDimensionalAgGridRowItem } from '../effects/reportDimensionalEffects';

export type IReportViewTabs = 'views' | 'explorer' | 'settings';

export enum VisualizationStatus {
  Current = 'current',
  Loading = 'loading',
  Updating = 'updating'
}

export enum DataTableStatus {
  Loading = 'loading',
  Updating = 'updating',
  Current = 'current',
  NoColumns = 'noColumns',
  Error = 'error'
}

export enum ColumnSearchStatus {
  Current = 'current',
  Loading = 'loading',
  Updating = 'updating'
}

export enum SettingsCardMode {
  DefaultRead = 'defaultRead',
  CleanUpdate = 'cleanUpdate',
  DirtyUpdate = 'dirtyUpdate',
  Loading = 'loading',
  ReadOnly = 'readOnly'
}

export interface IPdfExportData {
  detailTableData: { [key: string]: number | string }[];
  detailTableTotalsData: { [key: string]: number | string }[];
  accountSummaryTableData: { [key: string]: number | string }[];
}

export interface IReportExplorerState {
  activeNavigationPanelFilter: enumActiveNavigationPanelFilter;
  activeTab: IReportViewTabs;
  agGridCacheBuster: number;
  agGridColumns: any[] | null;
  agGridParentData: any[];
  allowedActions: Map<string, boolean>;
  chartType: EReportChartType;
  filterValuesByColumn: Map<string, Map<string, number>>;
  currentView: IRecordOf<IOgqlViewDetail>;
  dataTableStatus: DataTableStatus;
  dataTableTotalRowCount: number;
  dimensionalColumns: any[];
  dimensionalGroupRowData: IDimensionalAgGridRowItem[];
  dimensionalReportConfiguration: IReportConfiguration;
  dimensionalTableQueryStatuses: DataTableStatus[];
  editorDrawerOpen: boolean;
  editMode: EEditMode | null;
  fetchingPdfExportData: boolean;
  initialized: boolean;
  isEditModeDirty: boolean;
  isRefreshRowData: boolean;
  /** The pending row refresh is a load-time purge of the grid; the pivot cache must stay. */
  isRefreshRowKeepingCache: boolean;
  newColumnGroupType?: EGroupType;
  newRowGroupType?: EGroupType;
  paginationOffset: number;
  pdfExportData: IPdfExportData;
  pdfExportTooMuchData: boolean;
  pivotColumns: ColumnValueSegment[][];
  /** Ids of the column groups the `pivotColumns` request covered; null when unknown. */
  pivotColumnGroupIds: string[] | null;
  isLoadingReportDatasetLenses: boolean;
  reportDatasetLenses: IReportDatasetLens[];
  reportTotalRowCount: number;
  selectedColumnEditorLensId: string;
  selectedColumnGroupIndex?: number | null;
  selectedSegment: ColumnValueSegment[] | null;
  /**
   * The selected column's organize-by level path, outermost first, and its depth.
   *
   * `selectedSegment` stays the raw response tuple because consumers read it positionally; these
   * carry the depth-aware identity, which is what a rename has to be written under once a column
   * group can be broken down more than one level deep.
   */
  selectedColumnPath: ColumnValueSegment[] | null;
  selectedColumnLevel: number | null;
  selectedSource: IColumnDataSource | MultiHierarchyColumnDataSource | null;
  selectedRowGroup?: IRowGroup;
  selectedRowGroupIndex: number;
  settingsAboutCardMode: SettingsCardMode;
}

export type IChartProps = {
  chartCalculations: any; // IChartCalculations, but Chart.StackedBar in ReportVisualization complains
  chartType: EReportChartType;
  xTickLabelWidth?: number;
  yTickLabelHeight?: number;
  yTickLabelWidth?: number;
  xLabel?: string;
  yLabel?: string;
  xTickLabelFormatter?(domain: string | number): string;
  yTickLabelFormatter?(domain: string | number): string;
};

export interface IOrganizedByDateOption extends IOrganizedByOption {
  dateUnits: CalendarUnit[];
}

export interface IOrganizedByOption {
  columnName: string;
  columnDisplayName: string;
}

export interface IOrganizedByOptions {
  dateColumns?: IOrganizedByDateOption[];
  textColumns?: IOrganizedByOption[];
}
